//@ts-nocheck
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import formElementMixin from "@banno/jha-wc/src/js/mixins/form-element-mixin";
import { WorkflowTemplateGeneralViewMode } from "../../types/WorkflowTemplates/WorkflowTemplateTypeIndex";
import { getRequest } from "../../utils/jha-fetch";
import { Apis } from "../../utils/api-constants";
import { stringComparison } from "../../utils/app-utils";
import "./WorkflowTemplateSchedulerView";
import { WorkflowTemplateVariable } from "../../types/WorkflowTemplates/WorkflowTemplateVariable";
import { normalizeScheduler } from "../../utils/api-normalizer";

/**
 * Workflow Template Scheduler Tab
 *
 * This tab manages scheduler configuration for templates.
 * Uses the same SchedulerView component as regular workflows.
 *
 * Features:
 * - Configure CRON expressions for scheduled workflow execution
 * - Set timezone, start date/time, and end date/time
 * - Only available when TransferType is "Pull"
 *
 * Validation:
 * - If TransferType is NOT "Pull": Tab is valid (scheduler not applicable)
 * - If TransferType IS "Pull": Scheduler is REQUIRED (must be valid to save template)
 */
@customElement("workflow-template-scheduler-tab")
export class WorkflowTemplateSchedulerTab extends formElementMixin(LitElement) {
  static get is() {
    return "workflow-template-scheduler-tab";
  }

  @property({ type: String }) mode: WorkflowTemplateGeneralViewMode = "ADD";
  @property({ type: String }) workflowTemplateId: string = "";
  @property({ type: Boolean }) isReadOnly: boolean = false;
  @property({ type: Object }) templateData: any = null; // Passed from parent - no need to fetch
  @property({ type: String }) transferType: string = "";
  @property({ type: Array }) variables: WorkflowTemplateVariable[] = [];

  // Scheduler data (matches SchedulerView's formData structure)
  @state() schedulerData: any = {
    SchedulerName: "",
    Description: "",
    Expression: "",
    TimeZone: "",
    StartDate: "",
    StartHour: "",
    StartMinute: "",
    EndDate: "",
    EndTime: "",
    valid: false,
    isEdit: false,
    isRateDisabled: false,
    isCronDisabled: true,
  };

  // Loading state
  @state() apiLoading: boolean = false;

  // Dirty state
  @state() isDirty: boolean = false;

  // Flag to prevent marking dirty during initial load
  private isInitializing: boolean = true;

  // Original data for dirty detection
  private originalSchedulerData: any = {};

  static readonly styles = css`
    :host {
      display: block;
      padding: 20px;
    }

    .not-applicable {
      padding: 32px;
      text-align: center;
      border: 1px solid;
      border-radius: 4px;
    }

    @media (prefers-color-scheme: light) {
      .not-applicable {
        color: #666666;
        border-color: #e0e0e0;
        background-color: #f5f5f5;
      }
    }

    @media (prefers-color-scheme: dark) {
      .not-applicable {
        color: #a0a0a0;
        border-color: #3a3a3a;
        background-color: #2a2a2a;
      }
    }

    scheduler-view {
      display: block;
    }
  `;

  async connectedCallback() {
    super.connectedCallback();

    // Load existing scheduler data if in EDIT or VIEW mode
    if (this.workflowTemplateId) {
      await this.loadSchedulerData();
    } else {
      // ADD mode - new template starts clean (isDirty = false)
      // Dirty state is only set when user actually changes something via markDirty()
      // Scheduler is only required for Pull type
      this.isDirty = false;
    }

    this.validateAndEmit();
  }

  /**
   * Called after the first render - safe to start tracking dirty state
   * and emit initial validation to parent
   */
  firstUpdated() {
    setTimeout(() => {
      this.isInitializing = false;
      // Re-emit validation now that parent's event listeners are definitely attached
      this.validateAndEmit();
    }, 100);
  }

  /**
   * React to transferType changes from parent
   */
  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);

    if (changedProperties.has("transferType")) {
      this.validateAndEmit();
    }
  }

  /**
   * Load scheduler data for EDIT/VIEW mode
   * Uses templateData from parent if available (single fetch optimization)
   */
  async loadSchedulerData() {
    this.apiLoading = true;

    try {
      // Use templateData from parent if available, otherwise fetch (fallback for tests)
      const template = this.templateData || await getRequest(`${Apis.GET_WORKFLOW_TEMPLATE}/${this.workflowTemplateId}`);

      if (template && !template?.details?.length) {
        const rawSched = template.scheduler;

        // No scheduler payload, or empty map {} from API (Push templates) — keep default form state
        const isEmptySchedulerObject =
          rawSched &&
          typeof rawSched === "object" &&
          !Array.isArray(rawSched) &&
          Object.keys(rawSched).length === 0;
        if (rawSched == null || isEmptySchedulerObject) {
          this.apiLoading = false;
          this.validateAndEmit();
          return;
        }

        // CASING FIX: Normalize scheduler to camelCase (handles both API camelCase and any PascalCase)
        const sched = normalizeScheduler(rawSched);

        // Parse StartTime if present
        let startHour = "";
        let startMinute = "";
        if (sched.startTime) {
          const [hour, minute] = sched.startTime.split(":");
          startHour = hour;
          startMinute = minute;
        }

        // Parse expression to determine Type (rate/cron) and related fields
        let schedType = "";
        let rateValue = "";
        let rateConfig = "";
        let isRateDisabled = true;
        let isCronDisabled = true;

        if (sched.expression) {
          const expressionLower = sched.expression.toLowerCase();
          if (expressionLower.startsWith("rate(")) {
            schedType = "rate";
            isRateDisabled = false;
            isCronDisabled = true;
            // Parse rate expression: rate(5 minutes)
            const match = sched.expression.match(/rate\((\d+)\s+(\w+)\)/i);
            if (match) {
              rateValue = match[1];
              rateConfig = match[2];
            }
          } else if (expressionLower.startsWith("cron(")) {
            schedType = "cron";
            isRateDisabled = true;
            isCronDisabled = false;
          }
        }

        this.schedulerData = {
          SchedulerName: sched.schedulerName || "",
          Description: sched.description || "",
          Expression: sched.expression || "",
          TimeZone: sched.timezone || "",
          StartDate: sched.startDate || "",
          StartHour: startHour,
          StartMinute: startMinute,
          EndDate: sched.endDate || "",
          EndTime: sched.endTime || "",
          Type: schedType,
          RateValue: rateValue,
          RateConfig: rateConfig,
          valid: true,
          isEdit: true,
          isRateDisabled: isRateDisabled,
          isCronDisabled: isCronDisabled,
        };

        // Save original for dirty detection
        this.saveOriginalData();
        this.isDirty = false;
      }
    } catch (error) {
      console.error("Failed to load scheduler:", error);
    } finally {
      this.apiLoading = false;
      this.validateAndEmit();
    }
  }

  /**
   * Save current data as original for dirty detection
   */
  saveOriginalData() {
    this.originalSchedulerData = JSON.parse(JSON.stringify(this.schedulerData));
  }

  /**
   * Mark form as dirty
   */
  markDirty() {
    if (!this.isInitializing) {
      this.isDirty = true;
    }
  }

  /**
   * Handle scheduler change from SchedulerView
   * 
   * The SchedulerView dispatches this event with the updated formData.
   * We defensively copy the data to ensure synchronization even if object
   * references change (e.g., if either component re-assigns the object).
   */
  onSchedulerChange(e: CustomEvent) {
    // Defensive copy: merge event detail into schedulerData
    // This ensures data stays synchronized even if object references diverge
    if (e.detail) {
      Object.assign(this.schedulerData, e.detail);
    }
    this.markDirty();
    this.validateAndEmit();
  }

  /**
   * Check if transfer type is Pull (scheduler is required for Pull)
   */
  get isPullType(): boolean {
    return stringComparison(this.transferType, "Pull");
  }

  /**
   * Validate and emit validation state
   * - If TransferType is NOT "Pull": Always valid (scheduler not needed)
   * - If TransferType IS "Pull": Scheduler must be valid
   */
  validateAndEmit() {
    let isValid: boolean;

    if (!this.isPullType) {
      // Scheduler is not required for Push type
      isValid = true;
    } else {
      // Scheduler is required for Pull type
      isValid = this.schedulerData.valid === true;
    }

    this.emitValidation(isValid);
  }

  /**
   * Emit validation-change event to parent
   * @param valid - Whether the scheduler is valid (or not required for Push type)
   */
  emitValidation(valid: boolean) {
    this.dispatchEvent(
      new CustomEvent("validation-change", {
        detail: {
          tabName: "scheduler",
          valid,
          dirty: this.isDirty,
          midEdit: false,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Handle variable-added event from template-string-input
   * Bubble up to parent so it can add to Settings tab
   */
  onVariableAdded(e: CustomEvent) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent("variable-added", {
        detail: e.detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Get scheduler data for saving
   */
  getFormData() {
    if (!this.isPullType) {
      // Backend requires Scheduler present on template; Push uses empty object (not omitted / null)
      return { scheduler: {} };
    }

    return {
      scheduler: {
        schedulerName: this.schedulerData.SchedulerName,
        description: this.schedulerData.Description,
        expression: this.schedulerData.Expression,
        timezone: this.schedulerData.TimeZone,
        startDate: this.schedulerData.StartDate,
        startTime: this.schedulerData.StartHour && this.schedulerData.StartMinute
          ? `${this.schedulerData.StartHour}:${this.schedulerData.StartMinute}`
          : "",
        endDate: this.schedulerData.EndDate,
        endTime: this.schedulerData.EndTime,
      },
    };
  }

  render() {
    const isViewMode = this.mode === "VIEW";

    return html`
      <div>
        ${this.isPullType
        ? html`
        <workflow-template-scheduler-view data-auto-id="schedulerView" .formData=${this.schedulerData} .variables=${this.variables}
          .isReadOnlyUser=${isViewMode || this.isReadOnly} @onSchedulerChange=${this.onSchedulerChange}
          @variable-added=${this.onVariableAdded}>
        </workflow-template-scheduler-view>
        `
        : html`
        <div class="not-applicable">
          <h3>Scheduler Not Applicable</h3>
          <p>
            ${this.transferType
              ? `Scheduler is only available for Pull-type workflows. The current Transfer Type is "${this.transferType}".`
              : "Please select a Transfer Type in the Workflow Information tab. Scheduler is only available for Pull-type workflows."}
          </p>
        </div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "workflow-template-scheduler-tab": WorkflowTemplateSchedulerTab;
  }
}
